import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { lawyerData } from '../data/mock';

const HEADER_OFFSET = 96;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return false;
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top, behavior: 'smooth' });
  return true;
};

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Início', href: '/#inicio' },
    { label: 'Conheça a Dra.', href: '/#sobre' },
    { label: 'Áreas de Atuação', href: '/#areas' },
    { label: 'Diferenciais', href: '/#diferenciais' },
    { label: 'Contato', href: '/#analise' },
    { label: 'Fórum Jurídico', href: '/conteudo' }
  ];

  const goToLink = (href, event) => {
    if (event) event.preventDefault();
    setIsMobileMenuOpen(false);

    const hashIndex = href.indexOf('#');
    if (hashIndex !== -1) {
      const id = href.slice(hashIndex + 1);
      if (location.pathname === '/') {
        scrollToId(id);
        window.history.replaceState(null, '', `/#${id}`);
      } else {
        navigate(`/#${id}`);
      }
      return;
    }

    navigate(href);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/98 backdrop-blur-md shadow-sm border-b border-slate-200/60'
          : 'bg-[#f9f7f2]/95 backdrop-blur-sm border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {!isScrolled && (
          <div className="hidden lg:flex items-center justify-end gap-6 py-2.5 mb-3 text-sm text-[#1a2b48]/80">
            <a
              href={`tel:${lawyerData.phone.replace(/\D/g, '')}`}
              className="flex items-center gap-2 hover:text-[#b8962e] transition-colors"
            >
              <Phone size={14} />
              {lawyerData.phone}
            </a>
            <a
              href={`mailto:${lawyerData.email}`}
              className="flex items-center gap-2 hover:text-[#b8962e] transition-colors"
            >
              <Mail size={14} />
              {lawyerData.email}
            </a>
          </div>
        )}

        <div
          className={`hidden lg:flex lg:items-center lg:gap-5 xl:gap-8 ${
            isScrolled ? 'py-3' : 'py-2'
          }`}
        >
          <a
            href="/#inicio"
            onClick={(e) => goToLink('/#inicio', e)}
            className="flex items-center gap-3 shrink-0"
          >
            <img
              src={lawyerData.logo}
              alt={`${lawyerData.firmName} - ${lawyerData.firmTagline}`}
              className="h-12 xl:h-14 w-auto object-contain shrink-0"
            />
            <div className="flex flex-col justify-center gap-1">
              <span className="font-serif text-xl xl:text-2xl font-semibold tracking-wide text-[#1a2b48] leading-tight">
                {lawyerData.firmName}
              </span>
              <span className="font-sans text-[10px] xl:text-xs tracking-widest uppercase text-[#b8962e] leading-snug">
                <span className="block">Advocacia, Assessoria e</span>
                <span className="block">Consultoria Jurídica</span>
              </span>
            </div>
          </a>

          <nav className="flex flex-1 flex-nowrap items-center justify-end xl:justify-center gap-3 xl:gap-6 min-w-0">
            {navLinks.map((link) => (
              <a
                key={`${link.href}-${link.label}`}
                href={link.href}
                onClick={(e) => goToLink(link.href, e)}
                className="font-sans text-xs xl:text-sm font-medium text-[#1a2b48] hover:text-[#b8962e] transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="shrink-0">
            <Button
              onClick={() => goToLink('/#analise')}
              className="bg-[#d4af37] hover:bg-[#b8962e] text-[#1a2b48] rounded-md px-5 xl:px-6 py-2.5 text-sm font-semibold whitespace-nowrap"
            >
              Agendar Consulta
            </Button>
          </div>
        </div>

        <div
          className={`flex lg:hidden items-center justify-between gap-2 ${
            isScrolled ? 'py-2.5' : 'py-3'
          }`}
        >
          <a
            href="/#inicio"
            onClick={(e) => goToLink('/#inicio', e)}
            className="flex min-w-0 items-center gap-2.5"
          >
            <img
              src={lawyerData.logo}
              alt={lawyerData.firmName}
              className="h-10 w-10 shrink-0 object-contain"
            />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="font-serif text-[13px] font-semibold leading-tight text-[#0f1f3d] sm:text-sm">
                {lawyerData.firmName}
              </span>
              <span className="text-[9px] uppercase leading-snug tracking-[0.12em] text-[#b8962e] sm:text-[10px]">
                <span className="block truncate">Advocacia, Assessoria e</span>
                <span className="block truncate">Consultoria Jurídica</span>
              </span>
            </div>
          </a>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="shrink-0 p-2 text-[#0f1f3d] transition-colors hover:text-[#b8962e]"
            aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav className="lg:hidden pb-4 border-t border-slate-200/80 pt-4">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={`${link.href}-${link.label}`}
                  href={link.href}
                  onClick={(e) => goToLink(link.href, e)}
                  className="text-base font-medium text-slate-800 hover:text-[#b8962e] transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <Button
                onClick={() => goToLink('/#analise')}
                className="bg-[#d4af37] hover:bg-[#b8962e] text-[#1a1a1a] w-full font-semibold rounded-md"
              >
                Agendar Consulta
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
